
export class BridgeIntent {
    constructor(id, name, param, cbId) {
        this.id = id
        this.name = name // intentName
        this.param = param
        this.cbId = cbId
        this.category = BridgeMessage.category.intent
    }
}

export class BridgeCallback {
    constructor(id, param, error) {
        this.id = id
        this.param = param
        this.error = error
        this.category = BridgeMessage.category.callback
    }
}

export const BridgeMessage = {
    category: {
        intent: "BridgeIntent",
        callback: "BridgeCallback"
    }
};

export const PredefinedIntent = {
    showToast: "showToast",
    showSpinner: "showSpinner",
    hideSpinner: "hideSpinner",
    showAlert: "showAlert",
    showActionSheet: "showActionSheet",
    showPopUp: "showPopUp",
    showDatePicker: "showDatePicker",
    switchToTab: "switchToTab",
    pushPage: "pushPage",
    popPage: "popPage",
    modalPage: "modalPage",
    dismissPage: "dismissPage",
    showOverlay: "showOverlay",
    dismissOverlay: "dismissOverlay",
    executeRequest: "executeRequest",
    updateLanguage: "updateLanguage",
    phoneCall: "phoneCall",
    sendEmail: "sendEmail",
    logInfo: "logInfo",
    bridgeWebViewDidAppear: "bridgeWebViewDidAppear",
    bridgeWebViewWillDisappear: "bridgeWebViewWillDisappear",
    incompatibleSDK: "incompatibleSDK",
    openQRCodeScanner: "openQRCodeScanner",
    openInBrowser: "openInBrowser",
    saveToCache: "saveToCache",
    loadFromCache: "loadFromCache",
    shareImage: "shareImage",
    // updated at 06/30/2021
    getToken: "getToken",
    refreshToken: "refreshToken",
    // updated at 07/09/2021
    popToRoot: 'popToRoot',
    addReminderToCalendar: "addReminderToCalendar",
    removeReminderFromCalendar: "removeReminderFromCalendar",
    // updated at 07/12/2021
    shareUrl: "shareUrl",
    // updated at 08/03/2021
    getPosition: "getPosition",
    // updated at 11/02/2021 available for v1.6.111 at both ios & android
    grayLog: "grayLog",
    // updated at 08/09/2022
    removeFromCache: "removeFromCache",
};

let _bridgeSDK;

export function getBridgeSDK() {
    if (!_bridgeSDK) {
        _bridgeSDK = new BridgeSDK();
    }
    return _bridgeSDK
}

export const isInApp = (window.ReactNativeWebView !== undefined)

class BridgeSDK {
    constructor() {
        this._bridgeCbs = {};  // bridge callbacks
        this._init();
        this.predefinedKey = "bridgeSDK=>";
    }

    _init() {
        this.logInfo("userAgent: ", navigator.userAgent);

        let receiver = this.isUIWebView() ? window : document;
        receiver && receiver.addEventListener("message", (event) => {
            try {
                const message = JSON.parse(event.data);
                this._onMessageReceivedFromRN(message);
            } catch (e) {
                this.grayLogError({
                    name: e.name,
                    message: e.message,
                    stack: e.stack
                }, {func: 'bridge_sdk'});
                console.error("failed to parse message from RN", e);
            }
        }, false)
    }

    _onMessageReceivedFromRN(message) {
        if (!message) {
            console.error("Empty message");
            return;
        }

        this.logInfo("Message from RN: ", message);

        if (message.category === BridgeMessage.category.intent) {
            let {id, name, param, cbId} = message;
            this.performIntent(name, param, cbId);
        } else if (message.category === BridgeMessage.category.callback) {
            let {id, param, error} = message;
            let callback = this._bridgeCbs[id];
            if (callback) {
                if (id && (param !== undefined && param !== null)) {
                    let onSuccess = callback.onSuccess;
                    onSuccess && onSuccess(param);
                } else if (id && error) {
                    let onError = callback.onError;
                    onError && onError(error);
                }
                delete this._bridgeCbs[id];
            }
        } else {
            alert(message)
        }
    }

    _send(name, param, success, error) {
        if (!window.ReactNativeWebView) {
            console.error("window.ReactNativeWebView is null");
            return;
        }

        let message = {
            id: this.guid(),
            name: name,
            param: param,
            category: BridgeMessage.category.intent
        };

        if (success || error) {
            message.cbId = message.id;
        }

        if (message.cbId) {
            this._bridgeCbs[message.cbId] = {
                onSuccess: success,
                onError: error
            };
        }

        try {
            const msg = JSON.stringify(message)
            window && window.ReactNativeWebView.postMessage(msg);
        } catch (e) {
            this.grayLogError({
                name: e.name,
                message: e.message,
                stack: e.stack
            }, {func: 'bridge_sdk'})
        }
    }

    isUIWebView() {
        try {
            let userAgent = JSON.parse(navigator.userAgent);
            if (userAgent.appPlatform) {
                return userAgent.appPlatform === "ios"
            } else {
                return navigator.userAgent.toLowerCase().match(/\(ip.*applewebkit(?!.*(version|crios))/)
            }
        } catch (e) {
            this.grayLogError({
                name: e.name,
                message: e.message,
                stack: e.stack
            }, {func: 'bridge_sdk'})
            return false
        }
    }

    guid() {
        function S4() {
            return (((1+Math.random())*0x10000)|0).toString(16).substring(1);
        }
        return (S4()+S4()+"-"+S4()+"-"+S4()+"-"+S4()+"-"+S4()+S4()+S4());
    }

    performIntent(name, param, cbId) {
        switch (name) {
            case PredefinedIntent.updateLanguage:
                param.language && window && window.updateLanguage && window.updateLanguage(param.language);
                break;
            case PredefinedIntent.bridgeWebViewDidAppear:
                window && window.bridgeWebViewDidAppear && window.bridgeWebViewDidAppear(param);
                break;
            case PredefinedIntent.bridgeWebViewWillDisappear:
                window && window.bridgeWebViewWillDisappear && window.bridgeWebViewWillDisappear();
                break;
            default:
                break;
        }
    }

    showAlert(title, message, confirmTitle, confirm, cancelTitle, cancel) {
        let param = {
            title: title,
            message: message,
            confirmTitle: confirmTitle,
            cancelTitle: cancelTitle
        };
        this._send(PredefinedIntent.showAlert, param, confirm, cancel)
    }

    showToast(title) {
        let param = {
            title: title
        };
        this._send(PredefinedIntent.showToast, param)
    }

    showSpinner(key) {
        let param = {
            spinnerKey: key
        };
        this._send(PredefinedIntent.showSpinner, param)
    }

    hideSpinner(key) {
        let param = {
            spinnerKey: key
        };
        this._send(PredefinedIntent.hideSpinner, param)
    }

    pushPage(page, title, passProps, hideNavBar) {
        let param = {
            page: page,
            title: title,
            hideNavBar: hideNavBar,
            passProps: passProps
        };
        this._send(PredefinedIntent.pushPage, param)
    }

    /**
     * @Desc   pop page
     * @Params withProps is an object that you want to pass it to the previous page.
     * @Return
     */
    popPage(withProps) {
        this._send(PredefinedIntent.popPage, withProps)
    }

    popToRoot() {
        this._send(PredefinedIntent.popToRoot)
    }

    modalPage(page, title, passProps, hideNavBar) {
        let param = {
            page: page,
            title: title,
            hideNavBar: hideNavBar,
            passProps: passProps
        };
        this._send(PredefinedIntent.modalPage, param)
    }

    dismissPage(param) {
        this._send(PredefinedIntent.dismissPage, param)
    }

    showOverlay(page, passProps) {
        let param = {
            page: page,
            passProps: passProps
        };
        this._send(PredefinedIntent.showOverlay, param)
    }

    dismissOverlay() {
        this._send(PredefinedIntent.dismissOverlay)
    }

    /**
     * @Desc   timeout： 延时执行，单位ms; 默认是0，表示立即执行
     * @Params
     * @Return
     */
    switchToTab(index, timeout = 0) {
        let param = {
            tabIndex: index,
            timeout: timeout
        };
        this._send(PredefinedIntent.switchToTab, param)
    }

    showActionSheet(onClick) {
        this._send(PredefinedIntent.showActionSheet, {}, onClick)
    }

    showPopUp(title, list, onClick) {
        let param = {
            title: title,
            list: list
        };
        this._send(PredefinedIntent.showPopUp, param, onClick)
    }

    /**
     * @Desc
     * @Params options
     * options = {
     *     mode: date | time | datetime,
     *     initialDate: YYYY-MM-DDTHH:mm:ss
     * }
     * @Return
     */
    showDatePicker(options, onClick) {
        this._send(PredefinedIntent.showDatePicker, options || {}, onClick)
    }

    executeRequest(apiPath, params, success, fail) {
        let param = {
            apiPath: apiPath,
            params: params
        };
        this._send(PredefinedIntent.executeRequest, param, success, fail)
    }

    executeRequestWithUrl(url, header, method, params, success, fail) {
        let param = {
            url: url,
            header: header,  // http header, object
            method: method,  // GET || POST, default is POST
            params: params
        };
        this._send(PredefinedIntent.executeRequest, param, success, fail)
    }

    phoneCall(phoneNumber) {
        let param = {
            phone: phoneNumber
        };
        this._send(PredefinedIntent.phoneCall, param)
    }

    sendEmail(emailList, subject, body) {
        let param = {
            emails: emailList,
            subject: subject,
            body: body
        };
        this._send(PredefinedIntent.sendEmail, param)
    }

    logInfo(...data) {
        data && this._send(PredefinedIntent.logInfo, data)
    }

    openQRCodeScanner(title, passProps, success, fail) {
        let param = {
            title: title,
            passProps: passProps
        };
        this._send(PredefinedIntent.openQRCodeScanner, param, success, fail)
    }

    incompatibleSDK() {
        this._send(PredefinedIntent.incompatibleSDK)
    }

    openInBrowser(link) {
        let param = {
            link: link
        };
        this._send(PredefinedIntent.openInBrowser, param)
    }

    saveToCache(key, data, success, fail) {
        if (data === undefined || data === null) { return }

        let wrapper_key = key ? (this.predefinedKey + key) : this.predefinedKey;
        let param = {
            key: wrapper_key,
            data: data
        };
        this._send(PredefinedIntent.saveToCache, param, success, fail)
    }

    loadFromCache(key, success, fail) {
        let wrapper_key = key ? (this.predefinedKey + key) : this.predefinedKey;
        let param = {
            key: wrapper_key
        };
        this._send(PredefinedIntent.loadFromCache, param, success, fail)
    }

    removeFromCache(key) {
        let wrapper_key = key ? (this.predefinedKey + key) : this.predefinedKey;
        let param = {
            key: wrapper_key
        };
        this._send(PredefinedIntent.removeFromCache, param)
    }

    shareImage(img_base64, title, message) {
        let param = {
            img_base64: img_base64,
            title: title,
            message: message
        };
        this._send(PredefinedIntent.shareImage, param)
    }

    shareUrl(url, title, message) {
        let param = {
            url: url,
            title: title,
            message: message
        };
        this._send(PredefinedIntent.shareUrl, param)
    }

    getToken(success, fail) {
        this._send(PredefinedIntent.getToken, null, success, fail)
    }

    refreshToken(success, fail) {
        this._send(PredefinedIntent.refreshToken, null, success, fail)
    }

    /**
     * @Desc   add reminder event to system calendar
     * @Params reminderKey is a string that RN used as a key to cache reminder.
     *         For schedule reminder, please use MScheduleID as the reminderKey.
     * @Return
     */
    addReminderToCalendar(reminderKey, title, content) {
        let param = {
            reminderKey: reminderKey,
            title: title,
            content: content
        };
        this._send(PredefinedIntent.addReminderToCalendar, param)
    }

    removeReminderFromCalendar(reminderKey) {
        let param = {
            reminderKey: reminderKey
        };
        this._send(PredefinedIntent.removeReminderFromCalendar, param)
    }

    getPosition(success, fail) {
        this._send(PredefinedIntent.getPosition, null, success, fail)
    }

    /**
     * @Desc   grayLog api
     * @Params logType: string   info: object
     * @Return
     */
    _grayLog(logType, info) {
        let param = {
            logType: logType,
            info: info
        };
        this._send(PredefinedIntent.grayLog, param)
    }

    /**
     * @Desc   grayLog event
     * @Params eventName: string   additionalInfo: object
     * H5可用的eventName如下：find_doctor; book_appointment; favorite_doctor; share
     * @Return
     */
    grayLogEvent(eventName, additionalInfo) {
        let param = {
            eventName: eventName,
            additionalInfo: additionalInfo
        };
        this._grayLog('event', param)
    }

    /**
     * @Desc   grayLog network
     * @Params api: string   parameters: json string,   statusCode: number   errMsg?: string,  additionalInfo: object
     * @Return
     */
    grayLogNetwork(api, parameters, statusCode, errMsg, additionalInfo) {
        let param = {
            api: api,
            param: parameters,
            statusCode: statusCode,
            errMsg: errMsg,
            additionalInfo: additionalInfo
        };
        this._grayLog('network', param)
    }

    /**
     * @Desc   grayLog error
     * @Params error: object   additionalInfo: object
     * @Return
     */
    grayLogError(error, additionalInfo) {
        let param = {
            err: error,
            additionalInfo: additionalInfo
        };
        this._grayLog('error', param)
    }
}
